import { supabase } from '../../../lib/supabase/client';
import type { Task, TaskFormData } from '../types';

export const taskService = {
  /**
   * Tüm görevleri getir
   */
  async getTasks(): Promise<Task[]> {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Tasks fetch error:', error);
      throw error;
    }

    // PostgreSQL'den gelen veriyi frontend formatına çevir
    return (data || []).map(task => ({
      _id: task.id,
      userId: task.user_id,
      title: task.title,
      description: task.description || '',
      completed: task.completed,
      priority: task.priority as 'low' | 'medium' | 'high',
      dueDate: task.due_date,
      createdAt: task.created_at,
      updatedAt: task.updated_at,
    }));
  },

  /**
   * Tek bir görevi getir
   */
  async getTask(id: string): Promise<Task> {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Task fetch error:', error);
      throw error;
    }

    return {
      _id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description || '',
      completed: data.completed,
      priority: data.priority as 'low' | 'medium' | 'high',
      dueDate: data.due_date,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  /**
   * Yeni görev oluştur
   */
  async createTask(taskData: TaskFormData): Promise<Task> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Kullanıcı oturumu bulunamadı');
    }

    const { data, error } = await supabase
      .from('tasks')
      .insert({
        user_id: user.id,
        title: taskData.title,
        description: taskData.description || null,
        priority: taskData.priority,
        due_date: taskData.dueDate || null,
        completed: false,
      })
      .select()
      .single();

    if (error) {
      console.error('Task create error:', error);
      throw error;
    }

    return {
      _id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description || '',
      completed: data.completed,
      priority: data.priority as 'low' | 'medium' | 'high',
      dueDate: data.due_date,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  /**
   * Görevi güncelle
   */
  async updateTask(id: string, taskData: Partial<TaskFormData> & { completed?: boolean }): Promise<Task> {
    const updateData: any = {};

    if (taskData.title !== undefined) updateData.title = taskData.title;
    if (taskData.description !== undefined) updateData.description = taskData.description || null;
    if (taskData.priority !== undefined) updateData.priority = taskData.priority;
    if (taskData.dueDate !== undefined) updateData.due_date = taskData.dueDate || null;
    if (taskData.completed !== undefined) updateData.completed = taskData.completed;

    const { data, error } = await supabase
      .from('tasks')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Task update error:', error);
      throw error;
    }

    return {
      _id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description || '',
      completed: data.completed,
      priority: data.priority as 'low' | 'medium' | 'high',
      dueDate: data.due_date,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  /**
   * Görevi sil
   */
  async deleteTask(id: string): Promise<void> {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Task delete error:', error);
      throw error;
    }
  },

  /**
   * Görev durumunu değiştir (tamamlandı/bekliyor)
   */
  async toggleTaskComplete(id: string, completed: boolean): Promise<Task> {
    return this.updateTask(id, { completed });
  },
};

